// services/project/useProject.ts
import { useState } from "react";
import { projectService } from "@services/ProjectServices";
import { toastService } from "@services/ToastService";
import {
  ProjectOverview,
  SimplifiedProject,
} from "../shared/interfaces/ProjectsFiles";
export const useProject = () => {
  const [loading, setLoading] = useState(false);

  const createProject = async (data: Partial<ProjectOverview>) => {
    setLoading(true);
    try {
      const project = await projectService.create(data);
      toastService.success("Project created successfully");
      return project;
    } catch (error) {
      toastService.error("Failed to create project");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (params: {
    offset: number;
    limit: number;
    order: string;
    tab: string;
  }): Promise<{
    projects: SimplifiedProject[];
    total_count: number;
  }> => {
    if (params.tab === "shared") {
      return await projectService.getSharedProjects(params);
    } else if (params.tab === "private") {
      return await projectService.getMyProjects(params);
    } else if (params.tab === "All") {
      const [myProjects, sharedProjects] = await Promise.all([
        projectService.getMyProjects(params),
        projectService.getSharedProjects(params),
      ]);

      return {
        projects: [...myProjects.projects, ...sharedProjects.projects].sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ),
        total_count: myProjects.total_count + sharedProjects.total_count,
      };
    }
    return { projects: [], total_count: 0 };
  };

  const deleteProject = async (projectId: string) => {
    setLoading(true);
    try {
      await projectService.deleteProject(projectId);
      toastService.success("Project deleted");
    } catch (error) {
      toastService.error("Failed to delete project");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (
    projectId: string,
    data: Partial<ProjectOverview>
  ) => {
    try {
      const updatedProject = await projectService.updateProject(
        projectId,
        data
      );
      toastService.success("Project updated successfully");
      return updatedProject;
    } catch (error) {
      toastService.error("Failed to update project");
      throw error;
    }
  };

  const getProjectMembers = async (projectId: string) => {
    try {
      return await projectService.getProjectMembers(projectId);
    } catch (error) {
      console.error("Failed to fetch project members");
      throw error;
    }
  };

  const getProjectById = async (projectId: string) => {
    try {
      return await projectService.getProjectById(projectId);
    } catch (error) {
      console.error("Failed to fetch project details");
      throw error;
    }
  };
  const getSharedProject = async (sharedToken: string) => {
    try {
      return await projectService.getSharedProject(sharedToken);
    } catch (error) {
      console.error("Failed to fetch shared project");
      throw error;
    }
  };
  const getCountries = async () => {
    try {
      const response = await projectService.getCountries();
      return response;
    } catch (error) {
      console.error("Failed to fetch countries");
      throw error;
    }
  };
  const getDashboardInfo = async () => {
    try {
      return await projectService.getDashboardInfo();
    } catch (error) {
      console.error("Failed to load dashboard info");
      throw error;
    }
  };
  const grantProjectRole = async (
    projectId: string,
    email: string,
    roleId: number
  ) => {
    try {
      const response = await projectService.grantRole(projectId, email, roleId);
      return response?.status === 200;
    } catch (error) {
      console.error("Error granting project role:", error);
      return false;
    }
  };
  const revokeProjectRole = async (projectId: string, memberId: number) => {
    try {
      await projectService.revokeRole(projectId, memberId);
    } catch (err) {
      console.error("Failed to revoke project role:", err);
      throw err;
    }
  };
  const toggleProjectPublicAccess = async (
    projectId: string,
    isPublic: boolean
  ) => {
    try {
      const res = await projectService.toggleProjectPublicAccess(
        projectId,
        isPublic
      );
      return res?.status === 200;
    } catch (err) {
      console.error("Failed to update project public access:", err);
      return false;
    }
  };

  const getProjectsDropdown = async (query?: string) => {
    try {
      const response = await projectService.getProjectsDropdown(query || "");
      return response;
    } catch (error) {
      console.error("Failed to fetch projects dropdown");
      throw error;
    }
  };

  return {
    loading,
    createProject,
    fetchProjects,
    deleteProject,
    updateProject,
    getProjectMembers,
    getProjectById,
    getSharedProject,
    getCountries,
    getDashboardInfo,
    grantProjectRole,
    revokeProjectRole,
    toggleProjectPublicAccess,
    getProjectsDropdown,
  };
};
